import { Host, h } from "@stencil/core";
let id = 0;
export class CoreTextarea {
    constructor() {
        this.textareaId = `textarea-${++id}`;
        this.handleValueChange = () => {
            if (this.disabled)
                return;
            this.value = this.element.querySelector('textarea').value;
            this.coreTextareaValueChange.emit(this.value);
        };
        this.handleOnChange = () => {
            this.handleValueChange();
        };
        this.handleOnInput = () => {
            this.handleValueChange();
        };
        this.handleFocus = (event) => {
            event.stopImmediatePropagation();
            if (event.type === 'mousedown') {
                this.inFocus = false;
            }
            else {
                this.inFocus = true;
            }
        };
        this.getRequirementString = () => {
            if (this.requirement === 'mandatory') {
                return '·\u00a0obligatoire';
            }
            else if (this.requirement === 'optional') {
                return '·\u00a0facultatif';
            }
        };
        this.renderRequirement = () => {
            return this.requirement !== 'none' ? h("span", { class: "field-requirement typography-body-small-italic" }, this.getRequirementString()) : h("slot", { name: "custom-requirement" });
        };
        this.renderLabel = () => {
            return (h("label", { class: "label typography-body-small-bold", htmlFor: `${this.textareaId}`, onClick: e => {
                    this.handleFocus(e);
                } }, this.label, this.renderRequirement()));
        };
        this.renderDescription = () => {
            return this.description && h("span", { class: "description typography-body-small-regular" }, this.description);
        };
        this.renderHeader = (isRenderingDescription) => {
            return (h("div", { class: "textarea-header" }, this.renderLabel(), isRenderingDescription && this.renderDescription()));
        };
        this.renderErrorMessage = () => {
            return (h("span", { id: `${this.textareaId}-error-message`, class: "error-message typography-body-small-italic" }, this.errorMessage));
        };
        this.label = undefined;
        this.description = undefined;
        this.placeholder = undefined;
        this.requirement = 'mandatory';
        this.format = 'outside';
        this.disabled = false;
        this.errorMessage = null;
        this.value = undefined;
        this.inError = false;
        this.inFocus = false;
    }
    setErrorState(newValue) {
        if (this.errorMessage) {
            this.inError = true;
        }
        else {
            this.inError = false;
        }
        if (newValue) {
            this.errorMessage = newValue;
        }
    }
    connectedCallback() {
        this.setErrorState(this.errorMessage);
    }
    render() {
        return (h(Host, { key: '95e82e8f4b3f2ce554c188bd66850494e74d88ae' }, h("div", { key: 'bd88bd0ebf130fd3c989c9f21836caa1868854dd', class: `core-textarea textarea-format--${this.format} ${this.inError ? 'textarea-in-error' : ''} ${this.disabled ? 'textarea-disabled' : ''} ${this.inFocus ? 'textarea-focus' : ''}` }, this.format !== 'hidden' && this.renderHeader(this.description !== undefined), h("div", { key: '492946a5428004dcf26180739f3a243e6eb896e7', class: "textarea-wrapper" }, h("div", { key: 'aaa6ea93cb89676c453bc4ac4c5fc565ca732817', class: "field-content" }, h("textarea", { key: '3206b1d47fb611a1c28940e80530522997b6843f', id: this.textareaId, class: "textarea-field typography-body-medium-regular", "aria-invalid": this.inError, "aria-label": this.format === 'hidden' && this.label, "aria-describedby": this.inError && `${this.textareaId}-error-message`, value: this.value, onChange: () => {
                this.handleOnChange();
            }, onInput: () => {
                this.handleOnInput();
            }, onFocus: e => {
                this.handleFocus(e);
            }, onMouseDown: e => {
                this.handleFocus(e);
            }, onBlur: () => {
                this.inFocus = false;
            }, placeholder: this.placeholder, disabled: this.disabled, "aria-disabled": this.disabled }), (this.inError || this.disabled) && (h("i", { class: "field-indicator", "aria-hidden": "true" }, this.inError && (h("span", { class: "icon-box" }, h("svg", { style: { width: 'var(--number-size-icon-large)', height: 'var(--number-size-icon-large)' }, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("circle", { cx: "12", cy: "12", r: "11", fill: "#E71616" }), h("path", { d: "M13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5V13C11 13.5523 11.4477 14 12 14C12.5523 14 13 13.5523 13 13V5Z", fill: "white" }), h("path", { d: "M12 19.5C12.8284 19.5 13.5 18.8284 13.5 18C13.5 17.1716 12.8284 16.5 12 16.5C11.1716 16.5 10.5 17.1716 10.5 18C10.5 18.8284 11.1716 19.5 12 19.5Z", fill: "white" })))), this.disabled && (h("span", { class: "icon-box" }, h("core-icon", { name: "lock-solid", size: "number-size-icon-large", fill: "color-global-all-disabled-strong" })))))), this.inError && this.renderErrorMessage()))));
    }
    static get is() { return "core-textarea"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-textarea.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-textarea.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label that accompany the textarea field. It is required for **accessibility** concerns."
                },
                "attribute": "label",
                "reflect": false
            },
            "description": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Description is available on `outside` and `aside` format and will append below the label."
                },
                "attribute": "description",
                "reflect": false
            },
            "placeholder": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Placeholder inside the select."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "requirement": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'mandatory' | 'optional' | 'none'",
                    "resolved": "\"mandatory\" | \"none\" | \"optional\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available."
                },
                "attribute": "requirement",
                "reflect": false,
                "defaultValue": "'mandatory'"
            },
            "format": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'outside' | 'aside' | 'hidden'",
                    "resolved": "\"aside\" | \"hidden\" | \"outside\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Position the label in different place."
                },
                "attribute": "format",
                "reflect": false,
                "defaultValue": "'outside'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating the disable state of the textarea field. The `aria-disabled` attribute relies on this property."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMessage": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Display an error message under the field and set the field in error state."
                },
                "attribute": "error-message",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get states() {
        return {
            "value": {},
            "inError": {},
            "inFocus": {}
        };
    }
    static get events() {
        return [{
                "method": "coreTextareaValueChange",
                "name": "coreTextareaValueChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "errorMessage",
                "methodName": "setErrorState"
            }];
    }
}
//# sourceMappingURL=core-textarea.js.map
